/**
 * S9b · Your place in line — THE entrant waitlist view.
 *
 * This is the payoff of the referral loop: see position -> share -> position
 * moves. It is the ONE place this is rendered; S8 (after ordering) and S9
 * (account) both link here rather than growing their own copy of it.
 *
 * DATA CONTRACT
 *   - Position is DERIVED SERVER-SIDE (`get_waitlist_standing` ->
 *     `waitlist_ranked`). Nothing here ranks, sorts or counts to guess a
 *     position — that is the server's job and duplicating it would drift.
 *   - Movement + its cause come from `get_waitlist_movement`, which diffs the
 *     entrant's OWN `waitlist_rank_snapshots` and attributes each change to
 *     'own_score' or 'queue_shift'.
 *   - Downward movement IS displayed, honestly. K-1 (upward-only) governs
 *     outbound NOTIFICATIONS, never what the entrant sees when they look.
 *   - Both RPCs refuse any entry that is not the caller's. No other entrant's
 *     identity, score or position is read or rendered — position and total
 *     queue size only.
 *
 * SHARING: reuses `src/lib/share-card.ts` (the single share implementation).
 * No second share path is defined here.
 *
 * LEGAL GATES (L1): no scheme name/mark, no patent claim, NO present-tense or
 * future-tense issuance promise. Issuance is gated on the banking partner and
 * every lead is deferred by design, so this screen states position and cause
 * and explicitly declines to imply a delivery timeline.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getUser } from "@/services/auth";
import { listCardRequests, type CardRequestRow } from "@/services/card-requests";
import {
  getUserWaitlistEntry,
  getWaitlistStanding,
  getWaitlistMovement,
  type WaitlistEntryRow,
  type WaitlistStanding,
  type WaitlistMove,
  type WaitlistScoreEvent,
} from "@/services/waitlist";
import { buildShareUrl, publishCardShare } from "@/services/share";
import { shareCard } from "@/lib/share-card";
import { readDesignedCard } from "@/lib/designed-card";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { LAUNCH_PROGRAM_SLUG } from "@/services/programs";
import {
  Display,
  FunnelContainer,
  FunnelPage,
  Lede,
  SectionTitle,
  Wordmark,
} from "@/components/consumer/chrome";
import ContextSwitcher from "@/components/ContextSwitcher";
import { useSiteContent } from "@/hooks/useSiteContent";
import { S9_SECTION, S9_DEFAULTS } from "@/content/screens/s9";

/** Movement, in human terms. Both directions; cause named, never guessed. */
function moveSentence(m: WaitlistMove): string {
  const places = `${m.places_moved} ${m.places_moved === 1 ? "place" : "places"}`;
  if (m.direction === "up") {
    if (m.cause === "own_score") {
      const n = m.referral_events;
      if (n > 0) {
        return `You moved up ${places} — ${n} ${n === 1 ? "person" : "people"} used your code`;
      }
      return `You moved up ${places} — your score went up`;
    }
    return `You moved up ${places} as the queue changed around you`;
  }
  if (m.cause === "own_score") {
    return `You moved down ${places} after a change to your score`;
  }
  return `You moved down ${places} — others ahead of you gained places`;
}

/** Score events in plain language. "Why am I here?" answered without support. */
function eventSentence(e: WaitlistScoreEvent): string {
  const sign = e.delta > 0 ? `+${e.delta}` : `${e.delta}`;
  switch (e.reason) {
    case "initial":
      return `You joined the line (${sign})`;
    case "referral":
      return `Someone joined with your code (${sign})`;
    case "manual":
    case "manual_adjustment":
      return `Adjusted by the Cardmoji team (${sign})${e.note ? ` — ${e.note}` : ""}`;
    default:
      return `${e.reason.replace(/_/g, " ")} (${sign})`;
  }
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function S9bStanding() {
  const { t } = useSiteContent(S9_SECTION, S9_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState<WaitlistEntryRow | null>(null);
  const [standing, setStanding] = useState<WaitlistStanding | null>(null);
  const [moves, setMoves] = useState<WaitlistMove[]>([]);
  const [queueTotal, setQueueTotal] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const designedCard = readDesignedCard(LAUNCH_PROGRAM_SLUG);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await getUser();
      if (cancelled) return;
      if (!auth?.user) {
        setLoading(false);
        return;
      }
      const { data: rows } = await listCardRequests();
      const mine = ((rows as unknown as CardRequestRow[]) || []).filter((r) => r.user_id === auth.user.id);
      const tenantId = mine[0]?.tenant_id;
      if (!tenantId) {
        if (!cancelled) setLoading(false);
        return;
      }
      const { data: row } = await getUserWaitlistEntry(tenantId, auth.user.id);
      if (cancelled) return;
      const e = (row as WaitlistEntryRow | null) ?? null;
      setEntry(e);
      if (e) {
        const [s, m] = await Promise.all([getWaitlistStanding(e.id), getWaitlistMovement(e.id, 5)]);
        if (cancelled) return;
        setStanding(s.standing);
        setMoves(m.movement?.moves ?? []);
        setQueueTotal(m.movement?.queue_total ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function copyCode() {
    if (!entry?.referral_code) return;
    try {
      await navigator.clipboard.writeText(entry.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function onShare() {
    if (!entry?.referral_code) return;
    setSharing(true);
    setNote(null);
    try {
      const code = entry.referral_code;
      // Publish the PUBLIC target before the sheet opens — presentation data
      // only, exactly as S5 does. No order, no PII.
      if (designedCard.avatarUrl) {
        await publishCardShare({
          code,
          avatarUrl: designedCard.avatarUrl,
          colour: designedCard.colour,
          cardStyle: null,
          orientation: designedCard.orientation,
        });
      }
      const outcome = await shareCard({
        channel: "os",
        shareUrl: buildShareUrl(code),
        text: `My face, my card. Use my code ${code}.`,
        title: "cardmoji",
        composite:
          shareCardRef.current && designedCard.avatarUrl
            ? {
                node: shareCardRef.current,
                brandLine: "cardmoji",
              }
            : null,
      });
      if (outcome === "shared" || outcome === "shared_link") setNote("Shared.");
      else if (outcome === "downloaded_copied") setNote("Image downloaded and link copied — attach it in your app.");
      else if (outcome === "copied") setNote("Link copied.");
      else if (outcome === "failed") setNote("Couldn't prepare the share. Try again.");
    } catch {
      setNote("Couldn't prepare the share. Try again.");
    } finally {
      setSharing(false);
    }
  }

  return (
    <FunnelPage testId="s9b-standing" pb="pb-16">
      <div className="border-b border-foreground/10 bg-white">
        <FunnelContainer size="account" className="flex items-center gap-3 py-3.5 md:py-4">
          <Wordmark to="/" />
          <span className="flex-1" />
          <Link
            to="/account"
            data-testid="s9b-to-account"
            className="flex min-h-11 items-center rounded-full border border-foreground/15 px-4 text-[14px] font-medium text-ink hover:bg-muted"
          >
            Your account
          </Link>
          <ContextSwitcher variant="avatar" />
        </FunnelContainer>
      </div>

      <FunnelContainer size="account" className="pt-7">
        {loading ? (
          <p className="text-[14px] text-slate-muted">Loading…</p>
        ) : !entry || !standing ? (
          <>
            <Display>Nothing in line yet</Display>
            <Lede>Once you order a card you'll see your place in line here.</Lede>
            <Link
              to="/account"
              className="mt-6 inline-flex min-h-11 items-center rounded-full bg-ink px-5 text-[14px] font-semibold text-paper"
            >
              Back to your account
            </Link>
          </>
        ) : (
          <>
            {/* 1 · POSITION — server-derived, the thing they came to see. */}
            <div className="rounded-[22px] bg-ink px-6 py-7 text-paper">
              <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-paper/60">
                Your place in line
              </span>
              <div className="mt-1 flex items-end gap-3">
                <span
                  className="font-brand text-[64px] font-semibold leading-none tracking-[-0.03em] text-volt"
                  data-testid="s9b-position"
                >
                  {standing.position != null ? `#${standing.position}` : "—"}
                </span>
                {queueTotal != null ? (
                  <span className="pb-2 text-[14px] text-paper/70" data-testid="s9b-queue-total">
                    of {queueTotal} in line
                  </span>
                ) : null}
              </div>
              {/* HONESTY: position only. No timeline, no "ships soon", no claim
                  that the queue is moving toward fulfilment. */}
              <p className="m-0 mt-4 text-[14px] leading-[1.55] text-paper/85" data-testid="s9b-honesty">
                {t("s9b.honesty")}
              </p>
            </div>

            {/* 2 · MOVEMENT — human terms, both directions, cause named. */}
            <SectionTitle className="mb-3 mt-7">How you've moved</SectionTitle>
            <div className="rounded-2xl border border-foreground/10 bg-white" data-testid="s9b-movement">
              {moves.length === 0 ? (
                <p className="m-0 px-4 py-4 text-[14px] text-slate-muted">
                  No movement recorded yet — you'll see every change here, up or down.
                </p>
              ) : (
                moves.map((m, i) => (
                  <div
                    key={`${m.at}-${i}`}
                    className="flex items-start gap-3 border-b border-foreground/[0.07] px-4 py-3.5 last:border-b-0"
                    data-testid="s9b-move"
                    data-direction={m.direction}
                    data-cause={m.cause}
                  >
                    <span
                      className={`mt-px flex-none font-brand text-[15px] font-semibold ${
                        m.direction === "up" ? "text-leaf" : "text-[#C41112]"
                      }`}
                      aria-hidden
                    >
                      {m.direction === "up" ? "▲" : "▼"}
                    </span>
                    <span className="flex-1 text-[14px] leading-[1.5] text-ink">
                      {moveSentence(m)}
                      <span className="block text-[12.5px] text-slate-muted">
                        #{m.from_position} → #{m.to_position} · {formatWhen(m.at)}
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* 3 · REFERRALS + code + THE share affordance (shared module). */}
            {entry.referral_code ? (
              <>
                <SectionTitle className="mb-3 mt-7">Your code</SectionTitle>
                <div className="rounded-[20px] border border-foreground/10 bg-white p-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className="font-brand text-[30px] font-semibold tracking-[-0.01em] text-ink"
                      data-testid="s9b-referral-code"
                    >
                      {entry.referral_code}
                    </span>
                    <span className="flex-1" />
                    <button
                      type="button"
                      onClick={copyCode}
                      data-testid="s9b-copy-code"
                      className="flex min-h-11 cursor-pointer items-center rounded-full border border-foreground/20 px-[18px] text-[14px] font-semibold text-ink hover:border-ink"
                    >
                      {copied ? "Copied" : "Copy code"}
                    </button>
                    <button
                      type="button"
                      onClick={onShare}
                      disabled={sharing}
                      data-testid="s9b-share"
                      className="flex min-h-11 cursor-pointer items-center rounded-full bg-ink px-[18px] text-[14px] font-semibold text-paper disabled:opacity-60"
                    >
                      {sharing ? "Preparing…" : "Share your card"}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-2.5">
                    <span
                      className="font-brand text-[32px] font-semibold leading-none tracking-[-0.02em]"
                      data-testid="s9b-referral-count"
                    >
                      {standing.referral_count}
                    </span>
                    <span className="text-[13px] font-medium text-slate-muted">
                      {standing.referral_count === 1 ? "person has used your code" : "people have used your code"}
                    </span>
                  </div>
                  {note ? (
                    <p className="m-0 mt-3 text-[13px] font-medium text-grape" data-testid="s9b-share-note">
                      {note}
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}

            {/* 4 · RECENT SCORE EVENTS — plain language. */}
            <SectionTitle className="mb-3 mt-7">Why you're here</SectionTitle>
            <div className="rounded-2xl border border-foreground/10 bg-white" data-testid="s9b-events">
              {standing.events.length === 0 ? (
                <p className="m-0 px-4 py-4 text-[14px] text-slate-muted">No changes recorded yet.</p>
              ) : (
                standing.events.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-start gap-3 border-b border-foreground/[0.07] px-4 py-3.5 last:border-b-0"
                    data-testid="s9b-event"
                    data-reason={e.reason}
                  >
                    <span className="flex-1 text-[14px] leading-[1.5] text-ink">
                      {eventSentence(e)}
                      <span className="block text-[12.5px] text-slate-muted">{formatWhen(e.created_at)}</span>
                    </span>
                  </div>
                ))
              )}
            </div>

            <p className="mt-[22px] text-center text-[12.5px] leading-[1.55] text-foreground/70">
              {t("s9.footer.disclaimer")}
            </p>

            {/* Off-screen card node for the share composite. The compositor
                rasterises THIS node only — same designed-card model as the
                funnel, so the shared image matches the card on screen. */}
            <div aria-hidden className="pointer-events-none fixed -left-[9999px] top-0 w-[320px]">
              <div ref={shareCardRef} className="w-full">
                <DesignedCardFace card={designedCard} className="w-full" />
              </div>
            </div>
          </>
        )}
      </FunnelContainer>
    </FunnelPage>
  );
}
